// ==========================================
// Input row (3a / 1d)
// ==========================================
// 52px: field, TTS toggle, mic. Enter sends, Esc stops generation, "/" filters
// workflows inline, and the palette button (⌗) is what the chip rail collapses
// into at the compact density.
//
// Mic: tap toggles push-to-talk; holding for 400ms switches to hands-free (3d).
// ==========================================

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { MIC_HOLD_MS } from '../../models/buddy.constants';

@Component({
  selector: 'app-buddy-input-row',
  standalone: false,
  templateUrl: './buddy-input-row.component.html',
  styleUrls: ['./buddy-input-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyInputRowComponent implements OnDestroy {
  @Input() placeholder = 'Ask, or type / for workflows';
  @Input() ttsEnabled = true;
  @Input() micActive = false;
  @Input() handsFree = false;
  @Input() streaming = false;
  /** Live partial transcript, shown in grey until it commits (3d STT). */
  @Input() transcript = '';
  @Input() showPaletteButton = false;
  /** 1c's input row is field + mic only — no speaker toggle. */
  @Input() showTtsToggle = true;
  /** `gradient` is 3a's circular mic; `tinted` is 1c's rounded-square. */
  @Input() micVariant: 'gradient' | 'tinted' = 'gradient';

  @Output() submitted = new EventEmitter<string>();
  @Output() queryChanged = new EventEmitter<string>();
  @Output() micToggled = new EventEmitter<void>();
  @Output() micHeld = new EventEmitter<void>();
  @Output() ttsToggled = new EventEmitter<void>();
  @Output() paletteToggled = new EventEmitter<void>();
  @Output() stopRequested = new EventEmitter<void>();

  @ViewChild('field') field?: ElementRef<HTMLInputElement>;

  value = '';

  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private didHold = false;

  focus(): void {
    this.field?.nativeElement.focus();
  }

  setValue(next: string): void {
    this.value = next;
    if (this.field) {
      this.field.nativeElement.value = next;
    }
  }

  onInput(raw: string): void {
    this.value = raw;
    this.queryChanged.emit(raw);
  }

  onEnter(): void {
    const text = this.value.trim();
    if (!text) {
      return;
    }
    this.submitted.emit(text);
    this.setValue('');
    this.queryChanged.emit('');
  }

  /** Esc stops generation (3d). */
  onEscape(): void {
    if (this.streaming) {
      this.stopRequested.emit();
    }
  }

  onMicDown(): void {
    this.didHold = false;
    this.clearHold();
    this.holdTimer = setTimeout(() => {
      this.didHold = true;
      this.holdTimer = null;
      this.micHeld.emit();
    }, MIC_HOLD_MS);
  }

  onMicUp(): void {
    this.clearHold();
    if (!this.didHold) {
      this.micToggled.emit();
    }
    this.didHold = false;
  }

  onMicCancel(): void {
    this.clearHold();
    this.didHold = false;
  }

  get micLabel(): string {
    if (this.handsFree) {
      return 'Hands-free dictation on — release by staying silent';
    }
    return this.micActive ? 'Stop listening' : 'Start voice input';
  }

  get ttsLabel(): string {
    return this.ttsEnabled ? 'Turn speech off for this thread' : 'Turn speech on for this thread';
  }

  private clearHold(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearHold();
  }
}
